import React from 'react';

import { ParcelDetailScan } from '@lanterds/types';
import DetailSection from 'src/components/common/DetailSection/DetailSection';
import DataTable from 'src/components/common/DataTable/DataTable';
import { formatDate } from 'src/util/formatters';
import { Indexable } from 'src/interfaces/interfaces';
import { Box, Tooltip } from '@mui/material';
import InfoTwoToneIcon from '@mui/icons-material/InfoTwoTone';

interface ScanHistoryProps {
    data: Partial<ParcelDetailScan>[];
}

export default function ScanHistory({ data }: ScanHistoryProps) {
    const tipMessage = 'All times are local unless otherwise specified';
    const labels = [
        <>
            Scan Date{' '}
            <Tooltip arrow title={tipMessage} className="info-tooltip">
                <InfoTwoToneIcon color="primary" />
            </Tooltip>
        </>,
        'Scan Type',
        'Parcel Type',
        'Endpoint',
        'Location Name',
        'Address',
        'City',
        'State',
        'Damage',
        <>
            Date Sent At{' '}
            <Tooltip arrow title={tipMessage} className="info-tooltip">
                <InfoTwoToneIcon color="primary" />
            </Tooltip>
        </>,
        'Scanned At',
        'Scanned By',
        'Map',
    ];
    const keys = [
        'scanDate',
        'scanType',
        'containerType',
        'endPointNumber',
        'locationName',
        'addressStreet1',
        'addressCity',
        'addressState',
        'damage',
        'dateSentAt',
        'scannedAt',
        'scannedBy',
        'mapLink',
    ];

    const getGoogleMapUrl = (latitude: string, longitude: string) => {
        return `https://maps.google.com?q=${latitude},${longitude}`;
    };

    // TODO: Temporary, remove when BE services are updated
    const getBackwardsCompatibleScanDate = (datum: Partial<ParcelDetailScan>) => {
        if (datum?.scanDateLocal) {
            return formatDate(datum?.scanDateLocal);
        } else if (datum?.scanDateUtc) {
            return `${formatDate(datum?.scanDateUtc)} (UTC)`;
        } else {
            return formatDate((datum as any)?.scanDate);
        }
    };

    // TODO: Temporary, remove when BE services are updated
    const getBackwardsCompatibledDateSentAt = (datum: Partial<ParcelDetailScan>) => {
        if (datum?.dateSentAtLocal) {
            return formatDate(datum?.dateSentAtLocal);
        } else if (datum?.dateSentAtUtc) {
            return `${formatDate(datum?.dateSentAtUtc)} (UTC)`;
        } else {
            return formatDate((datum as any)?.dateSentAt);
        }
    };

    let transformedData: Array<Indexable> = [];

    if (data && data?.length > 0) {
        transformedData = data.map((datum) => {
            const mapUrl =
                datum?.mapLatitude && datum.mapLongitude ? getGoogleMapUrl(datum.mapLatitude, datum.mapLongitude) : '';

            return {
                scanDate: getBackwardsCompatibleScanDate(datum),
                scanType: datum?.scanType + (datum.scanSubType ? ` - ${datum.scanSubType}` : ''),
                scannedAt: datum?.scannedAt,
                scannedBy: datum?.scannedBy,
                damage: datum?.damage,
                dateSentAt: getBackwardsCompatibledDateSentAt(datum),
                containerType: datum?.containerType,
                endPointNumber: datum?.endPointNumber,
                locationName: datum?.locationName,
                addressStreet1: datum?.addressStreet1,
                addressCity: datum?.addressCity,
                addressState: datum?.addressState,
                mapLink: mapUrl ? (
                    <a target="_blank" rel="noreferrer" href={mapUrl}>
                        Map
                    </a>
                ) : (
                    ''
                ),
            };
        });
    }

    const content =
        transformedData && transformedData?.length ? <DataTable labels={labels} keys={keys} data={transformedData} /> : <></>;

    return (
        <DetailSection id="scan-history" title="Scan History">
            {content}
        </DetailSection>
    );
}

ScanHistory.defaultProps = {
    data: [],
};
